<template>
  <div v-if="!permsLoaded" class="p-6">
    <p class="text-gray-500 dark:text-gray-400">Loading...</p>
  </div>

  <div class="p-6 dark:text-gray-50" v-else-if="canAdd || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-1.5 rounded-md hover:bg-gray-700 text-sm flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back
      </button>
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEditMode ? 'Edit' : 'Add' }} SNMP Walk OID</h1>
    </div>

    <form
      @submit.prevent="submitForm"
      class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 max-w-2xl"
    >
      <!-- OID -->
      <div class="flex flex-col">
        <label for="oid" class="mb-1 font-medium">
          Walk OID<span class="text-red-500">*</span>
        </label>
        <input
          id="oid"
          v-model="formData.oid"
          type="text"
          required
          placeholder="e.g., 1.3.6.1.2.1.31.1.1"
          :class="inputClass('oid')"
        />
        <p class="text-sm text-gray-500 mt-1">
          SNMP OID to walk. This should be a table OID (not including the index).
        </p>
        <FieldError :message="fieldErrors.oid" />
      </div>

      <!-- Description -->
      <div class="flex flex-col">
        <label for="description" class="mb-1 font-medium">
          Description <span class="text-gray-500 text-sm">(optional)</span>
        </label>
        <input
          id="description"
          v-model="formData.description"
          type="text"
          placeholder="e.g., Interface table, CPU statistics"
          class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p class="text-sm text-gray-500 mt-1">Brief description of what this walk OID covers</p>
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-2 pt-4">
        <button type="button" @click="goBack" class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
          Cancel
        </button>
        <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          {{ isEditMode ? 'Update' : 'Create' }} Walk OID
        </button>
      </div>
    </form>
  </div>

  <PageError v-else :error="'Permission denied'" :errorCode="'403'" />
</template>

<script>
import { createSNMPWalk, getSNMPWalkById, updateSNMPWalk } from "@/services/snmpProfileService";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddSNMPWalk",
  components: { FieldError, PageError },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      fieldErrors: {},
      formData: {
        oid: "",
        description: "",
      },
      canAdd: false,
      isLoading: false,
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    profileId() {
      return this.$route.params.profileId;
    },
    walkId() {
      return this.$route.params.walkId;
    },
    isEditMode() {
      return !!this.walkId;
    },
    currentSchema() {
      return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null;
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.checkPermissions();
    if (this.isEditMode) {
      await this.loadExistingWalk();
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add SNMP Walk");
      }
    },
    async loadExistingWalk() {
      this.isLoading = true;
      try {
        const walk = await getSNMPWalkById(this.walkId, this.currentSchema);
        this.formData.oid = walk.oid;
        this.formData.description = walk.description || "";
      } catch (error) {
        console.error("Error loading walk:", error);
        alert("Failed to load walk data");
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    inputClass(field) {
      return [
        "border rounded px-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600",
        this.fieldErrors[field] ? "border-red-500 ring-2 ring-red-500" : "border-gray-300 focus:ring-blue-500",
      ];
    },
    goBack() {
      this.$router.push({ name: "SNMPProfileDetails", params: { id: this.profileId }, query: this.tenant.addSchemaParam() });
    },
    async submitForm() {
      this.fieldErrors = {};

      // Validate OID format
      const oidRegex = /^\d+(\.\d+)+$/;
      if (!oidRegex.test(this.formData.oid)) {
        this.fieldErrors.oid = "OID must be numeric with dots (e.g., 1.3.6.1.2.1.31.1.1)";
        return;
      }

      const data = {
        profile: parseInt(this.profileId),
        oid: this.formData.oid,
        description: this.formData.description,
      };

      try {
        if (this.isEditMode) {
          await updateSNMPWalk(this.walkId, data, this.currentSchema);
        } else {
          await createSNMPWalk(data, this.currentSchema);
        }
        this.goBack();
      } catch (error) {
        const errData = error?.response?.data;
        if (errData) {
          Object.keys(errData).forEach((key) => {
            this.fieldErrors[key] = Array.isArray(errData[key]) ? errData[key][0] : errData[key];
          });
        }
        console.error(`Error ${this.isEditMode ? "updating" : "creating"} walk:`, error);
      }
    },
  },
};
</script>
